// src/components/deliveryActivity/EntregasHeatmap.jsx
// Actividad de entregas: tiles de resumen + heatmap semanal de las últimas 53
// semanas ISO (estilo contribution graph, celda = semana). Datos reales vía
// useDeliveredWeekly — por comercio (companyId, anclado a deliveredAt) o por
// chofer (driverId, entregados por semana de creación; ver hook). Granularidad
// semanal porque el backend no expone serie diaria y armarla costaría 365
// requests por sujeto.
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PackageCheck, RotateCw } from "lucide-react";
import { cn } from "../../utils/cn";
import { Button } from "../ui/Button";
import { formatNumberCL } from "../../utils/formatters";
import useDeliveredWeekly from "./useDeliveredWeekly";

// Ramp secuencial de un solo matiz (marca) sobre tokens: 0 = sin entregas,
// 1..4 = intensidad relativa al máximo de la serie. Alfa sobre --brand-500 →
// funciona en light/dark y en los temas de marca sin código extra.
const LEVEL_CLASSES = [
  "bg-bg-subtle",
  "bg-brand-500/25",
  "bg-brand-500/45",
  "bg-brand-500/70",
  "bg-brand-600",
];

// Nivel 1..4 relativo al máximo de la serie (0 = sin entregas). Monotónico con
// la leyenda y sin degenerar: la mejor semana siempre pinta nivel 4, incluso
// con pocas semanas activas o conteos todos iguales (cuartiles ahí dejaban a
// TODA la serie en el tono más claro).
const levelOf = (count, max) => {
  if (typeof count !== "number" || count === 0 || !max) return 0;
  return Math.min(4, Math.max(1, Math.ceil((count / max) * 4)));
};

function Tile({ label, value, hint }) {
  return (
    <div className="rounded-lg bg-bg-muted px-3 py-2.5 min-w-0">
      <div className="text-[0.6875rem] font-semibold uppercase tracking-wider text-fg-muted truncate">
        {label}
      </div>
      <div className="mt-0.5 text-xl font-bold text-fg tabular-nums truncate">{value}</div>
      {hint && <div className="text-xs text-fg-muted truncate">{hint}</div>}
    </div>
  );
}

// Exactamente uno de companyId/driverId. `subtitle` permite ajustar la bajada
// al sujeto ("del comercio", "asignados al chofer", ...).
export default function EntregasHeatmap({ companyId, driverId, subtitle }) {
  const { t, i18n } = useTranslation();
  const [reloadKey, setReloadKey] = useState(0);
  const { series, loading } = useDeliveredWeekly({ companyId, driverId }, reloadKey);
  const [hovered, setHovered] = useState(null);

  // Al cambiar la serie (otra empresa, retry) el índice hovered viejo quedaría
  // pegado con el tooltip de la empresa anterior sin mouse encima.
  useEffect(() => {
    setHovered(null);
  }, [series]);

  const fmtDay = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { day: "numeric", month: "short" }),
    [i18n.language],
  );
  const fmtMonth = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { month: "short" }),
    [i18n.language],
  );

  const stats = useMemo(() => {
    if (!Array.isArray(series)) return null;
    const known = series.filter((w) => typeof w.count === "number");
    const total = known.reduce((s, w) => s + w.count, 0);
    const active = known.filter((w) => w.count > 0).length;
    const best = known.reduce((b, w) => (b === null || w.count > b.count ? w : b), null);
    return {
      total,
      active,
      weeks: series.length,
      avg: known.length ? Math.round(total / known.length) : 0,
      best,
      max: best ? best.count : 0,
    };
  }, [series]);

  // Etiquetas de mes bajo la tira: en la primera semana cuyo lunes cambia de
  // mes, salteando etiquetas a menos de 4 celdas de la anterior (colisionan
  // cuando la tira se comprime, p.ej. en mobile).
  const monthLabels = useMemo(() => {
    if (!Array.isArray(series)) return [];
    let last = -Infinity;
    return series.map((w, i) => {
      const isBoundary = i === 0 || w.start.getMonth() !== series[i - 1].start.getMonth();
      if (!isBoundary || i - last < 4) return null;
      last = i;
      return fmtMonth.format(w.start);
    });
  }, [series, fmtMonth]);

  // Sin sujeto (p.ej. empresa no populada) no hay serie posible: mejor no
  // renderizar el card que ofrecer un "Reintentar" que nunca resuelve.
  if (!companyId && !driverId) return null;

  return (
    <div className="rounded-lg border border-border bg-bg p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-700">
            <PackageCheck className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-fg">{t("Actividad de entregas")}</h3>
            <p className="text-xs text-fg-muted">
              {subtitle || t("Pedidos entregados por semana · últimos 12 meses")}
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        // Skeleton con la forma real: 4 tiles + tira de 53 celdas.
        <div className="mt-4 space-y-4" aria-hidden="true">
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-[4.25rem] rounded-lg bg-bg-subtle animate-pulse" />
            ))}
          </div>
          <div className="grid gap-[3px]" style={{ gridTemplateColumns: "repeat(53, minmax(0, 1fr))" }}>
            {Array.from({ length: 53 }).map((_, i) => (
              <div key={i} className="aspect-square rounded-[3px] bg-bg-subtle animate-pulse" />
            ))}
          </div>
        </div>
      ) : !series ? (
        <div className="mt-4 flex flex-col items-center gap-3 rounded-lg border border-dashed border-border py-6 text-center">
          <p className="text-sm text-fg-muted">{t("No se pudo cargar la actividad de entregas")}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setReloadKey((k) => k + 1)}
            iconLeft={<RotateCw className="w-4 h-4" />}
          >
            {t("Reintentar")}
          </Button>
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            <Tile label={t("Entregadas · 12 meses")} value={formatNumberCL(stats.total)} />
            <Tile label={t("Promedio semanal")} value={formatNumberCL(stats.avg)} />
            <Tile
              label={t("Mejor semana")}
              value={stats.best ? formatNumberCL(stats.best.count) : "—"}
              hint={stats.best ? `${t("Semana del")} ${fmtDay.format(stats.best.start)}` : null}
            />
            <Tile
              label={t("Semanas con entregas")}
              value={`${stats.active}/${stats.weeks}`}
            />
          </div>

          {/* Tira: 53 semanas, de la más antigua (izq.) a la vigente (der.).
              En pantallas angostas scrollea horizontal dentro del card. */}
          {/* pt-9 reserva el alto del tooltip DENTRO del scroll container:
              overflow-x-auto también recorta el overflow vertical, así que el
              tooltip no puede vivir fuera de la caja. */}
          <div className="overflow-x-auto pb-1">
            <div className="relative min-w-[30rem] pt-9">
              {hovered !== null && series[hovered] && (
                <div
                  className="pointer-events-none absolute top-0 z-10 whitespace-nowrap rounded-md border border-border bg-bg px-2.5 py-1 text-xs text-fg shadow-elevated"
                  style={{
                    left: `${((hovered + 0.5) / series.length) * 100}%`,
                    // Clamp suave en los bordes para no desbordar el card.
                    transform: `translateX(${hovered < 4 ? "0" : hovered > series.length - 5 ? "-100%" : "-50%"})`,
                  }}
                >
                  <span className="font-semibold tabular-nums">
                    {typeof series[hovered].count === "number"
                      ? formatNumberCL(series[hovered].count)
                      : "—"}
                  </span>{" "}
                  {t("entregas")} · {t("Semana del")} {fmtDay.format(series[hovered].start)}
                  {hovered === series.length - 1 && ` (${t("en curso")})`}
                </div>
              )}
              <div
                className="grid gap-[3px]"
                style={{ gridTemplateColumns: `repeat(${series.length}, minmax(0, 1fr))` }}
                onMouseLeave={() => setHovered(null)}
              >
                {series.map((w, i) => (
                  <div
                    key={w.start.getTime()}
                    role="img"
                    aria-label={`${t("Semana del")} ${fmtDay.format(w.start)}: ${
                      typeof w.count === "number"
                        ? `${formatNumberCL(w.count)} ${t("entregas")}`
                        : t("sin dato")
                    }`}
                    onMouseEnter={() => setHovered(i)}
                    className={cn(
                      "aspect-square rounded-[3px] transition-colors",
                      typeof w.count === "number"
                        ? LEVEL_CLASSES[levelOf(w.count, stats.max)]
                        : "bg-bg-subtle border border-dashed border-border",
                      hovered === i && "ring-1 ring-brand-500",
                    )}
                  />
                ))}
              </div>
              <div
                className="mt-1 grid gap-[3px] text-[0.625rem] leading-tight text-fg-subtle"
                style={{ gridTemplateColumns: `repeat(${series.length}, minmax(0, 1fr))` }}
                aria-hidden="true"
              >
                {monthLabels.map((m, i) => (
                  <span key={series[i].start.getTime()} className="overflow-visible whitespace-nowrap">
                    {m || ""}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2">
            {stats.total === 0 ? (
              <p className="text-xs text-fg-muted">{t("Sin entregas en los últimos 12 meses")}</p>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-1.5 text-[0.6875rem] text-fg-muted" aria-hidden="true">
              {t("Menos")}
              {LEVEL_CLASSES.map((cls) => (
                <span key={cls} className={cn("h-2.5 w-2.5 rounded-[2px]", cls)} />
              ))}
              {t("Más")}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
